import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from '../../../../../auth/services/profile.service';
import { QuoteRequest } from '../../../../../models/offer.request.model';
import { AdminService } from '../../services/admin.service';
import { PartnerService } from '../../services/partner.service';
import { AlertNotificationsService } from '../../../../../../services/alert-notifications.service';
import { ViewStatus } from '@uranda/ui';
import { ContactRequest, ContactStatus } from '../../../../../models/contact-request.model';
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component';
import { PartnerContactRequest } from '../../../../../models/partner-contact-request.model';
import { WalletService } from '../../../../../../services/wallet.service';
import { Utils } from '../../../../../utils/utils';
import { isHandledPermissionError } from '../../../../../interceptor/http-interceptor';

import { HIDDEN_CONTACT_LABEL } from '../../../../../utils/partner-permissions.util';
@Component({
  selector: 'app-contact-request-details',
  templateUrl: './contact-request-details.component.html',
  styleUrls: ['./contact-request-details.component.css'],
  standalone: false,
})
export class ContactRequestDetailsComponent implements OnInit {
  readonly hiddenContactLabel = HIDDEN_CONTACT_LABEL;

  contactRequestId?: string;
  contactRequest!: ContactRequest;
  partnerContactRequest!: PartnerContactRequest;
  isAdmin = false;
  internalNotes = '';
  viewStatus = ViewStatus.LOADING;
  baseRoute = '/partners';
  @ViewChild('confirmationModal')
  confirmationModal!: ConfirmationModalComponent;
  @Output() actionPerformed = new EventEmitter<void>();
  ViewStatus = ViewStatus;

  constructor(
    protected profileService: ProfileService,
    private adminService: AdminService,
    private partnerService: PartnerService,
    private notificationService: AlertNotificationsService,
    private translateService: TranslateService,
    private walletService: WalletService,
  ) {
    this.isAdmin = profileService.isAdmin;
    if (this.isAdmin) {
      this.baseRoute = '/admin';
    }
  }

  get isWalletDebtExceeded(): boolean {
    return this.walletService.isBlocked;
  }

  async ngOnInit() {
    if (this.contactRequestId) {
      this.viewStatus = ViewStatus.LOADING;
      await this.getContactRequestDetails();
    }
  }

  get pageTitle() {
    if (this.isAdmin) {
      const partnerName = this.contactRequest?.partner?.name;
      return partnerName
        ? this.translateService.instant('Contact request to {{destination}}', { destination: partnerName })
        : this.translateService.instant('Contact request');
    }
    const originCountry = Utils.resolveCountryName(
      this.translateService,
      this.contactRequest?.lead?.countryCode,
      this.contactRequest?.lead?.country,
    );
    return originCountry
      ? this.translateService.instant('Contact request from {{origin}}', { origin: originCountry })
      : this.translateService.instant('Contact request');
  }

  get formattedCountry(): string {
    const lead = this.contactRequest?.lead;
    if (!lead) return '';
    return Utils.resolveCountryName(this.translateService, lead.countryCode, lead.country);
  }

  async getContactRequestDetails() {
    if (!this.contactRequestId) return;
    if (this.isAdmin) {
      this.contactRequest = await this.adminService.getContactRequest(
        this.contactRequestId
      );
    } else {
      this.partnerContactRequest = await this.partnerService.getContactRequest(
        this.contactRequestId
      );
      this.contactRequest = this.partnerContactRequest.contactRequest;
    }
    this.internalNotes = '';
    this.viewStatus = ViewStatus.READY;
  }

  async unlockContactRequest(phoneDigits: string) {
    try {
      if (!this.partnerContactRequest?.id) {
        console.error('Contact request ID is missing');
        return;
      }
      await this.partnerService.unlockPhoneNumber(
        this.partnerContactRequest.id.toString(),
        phoneDigits
      );
      this.notificationService.success(
        this.translateService.instant('Phone number unlocked successfully')
      );

      await this.getContactRequestDetails();

      this.actionPerformed.emit();
    } catch (error) {
      console.error(error);
      // A PERMISSION_REQUIRED 403 already shows its own warning toast (see http-interceptor).
      if (!isHandledPermissionError(error)) {
        this.notificationService.error(
          this.translateService.instant('Error unlocking phone number')
        );
      }
    }
  }

  async updateLabel(event: any) {
    if (this.contactRequest) {
      try {
        await this.partnerService.updateLabel(
          Number(this.contactRequestId),
          event
        );
        await this.getContactRequestDetails();

        this.actionPerformed.emit();
      } catch (error) {
        if (!isHandledPermissionError(error)) {
          this.notificationService.warning(
            'Something went wrong. Please try again later.'
          );
        }
      }
    }
  }

  statusBadgeOptions = [
    { value: ContactStatus.PENDING, label: 'Pending', color: 'bg-warning' },
    { value: ContactStatus.ACCEPTED, label: 'Accepted', color: 'bg-info' },
    { value: ContactStatus.UNLOCKED, label: 'Unlocked', color: 'bg-success text-white' },
    { value: ContactStatus.REJECTED, label: 'Rejected', color: 'bg-danger text-white' },
    { value: ContactStatus.ARCHIVED, label: 'Archived', color: 'bg-secondary' },
    { value: ContactStatus.EXPIRED, label: 'Expired', color: 'bg-dark text-white' },
    { value: ContactStatus.UNVERIFIED, label: 'Unverified', color: 'bg-light text-dark' },
  ];

  showPhoneNumberConfirmation() {
    this.confirmationModal.open({
      title: this.translateService.instant('Confirm Action'),
      message: this.translateService.instant(
        'Are you sure you want to show the phone number?'
      ),
      submessage: this.translateService.instant(
        "This action will accept the contact request and reveal the customer's phone number."
      ),
      confirmText: this.translateService.instant('Unlock'),
      cancelText: this.translateService.instant('Cancel'),
      confirmButtonClass: 'btn-success',
      size: 'md',
      requirePhoneDigits: true,
      cost: this.partnerContactRequest?.cost,
    });
  }

  onPhoneNumberConfirmed(data: { phoneDigits?: string }): void {
    if (data.phoneDigits) {
      this.unlockContactRequest(data.phoneDigits);
    }
  }

  onPhoneNumberDismissed(): void { }

  protected readonly ContactStatus = ContactStatus;
}
